window.Shipping = (() => {
  const SHIPPING_PRODUCT_ID = 8046829895789;

  const VARIANTS = {
    moto_local: 45004822741101,
    moto_nacional: 45004822773869,
    vehiculo_local: 45004822806637,
    vehiculo_nacional: 45004822839405,
    carga_local: 45004822872173,
    carga_nacional: 45004822904941,
  };

  const SHIPPING_TYPES = ["moto", "vehiculo", "carga"];
  const SHIPPING_VARIANT_IDS = new Set(Object.values(VARIANTS).map(Number));
  const LOCATION_STORAGE_KEY = "customer_location";
  const FETCH_STATE_KEY = "__valhallaShippingFetchState";
  const SHIPPING_SYNC_SOURCE = "valhalla-shipping";

  const fetchState =
    window[FETCH_STATE_KEY] ||
    (() => {
      const state = {
        rawFetch: window.fetch.bind(window),
        wrapped: false,
      };

      try {
        Object.defineProperty(window, FETCH_STATE_KEY, {
          value: state,
          configurable: false,
          enumerable: false,
          writable: false,
        });
      } catch (err) {
        window[FETCH_STATE_KEY] = state;
      }

      return state;
    })();

  const rawFetch = fetchState.rawFetch;

  const shippingState = {
    isProcessing: false,
    rerunRequested: false,
    externalSyncPending: false,
    activePromise: null,
    lastReason: "idle",
  };

  let lastShippingType = "moto";
  let externalCartMutationTimer = null;
  let shippingVisualTimer = null;

  function getCartItemsComponents() {
    return Array.from(document.querySelectorAll("cart-items-component")).filter(
      (component) => component instanceof HTMLElement
    );
  }

  function setShippingVisualUpdating(isUpdating) {
    clearTimeout(shippingVisualTimer);
    document.documentElement.classList.toggle("valhalla-shipping-updating", isUpdating);

    getCartItemsComponents().forEach((itemsEl) => {
      itemsEl.classList.toggle("valhalla-shipping-updating", isUpdating);

      if (isUpdating) {
        itemsEl.setAttribute("aria-busy", "true");
        itemsEl.setAttribute("aria-live", "polite");
      } else {
        itemsEl.removeAttribute("aria-busy");
        itemsEl.removeAttribute("aria-live");
      }
    });
  }

  function setCartDrawerVisualRefresh(itemsComponents, isRefreshing) {
    itemsComponents.forEach((itemsEl) => {
      itemsEl.classList.toggle("valhalla-cart-drawer-refreshing", isRefreshing);

      if (!isRefreshing) {
        itemsEl.classList.add("valhalla-cart-drawer-refreshed");
        const clearRefreshedClass = () => {
          setTimeout(() => {
            itemsEl.classList.remove("valhalla-cart-drawer-refreshed");
          }, 240);
        };

        if (typeof window.requestAnimationFrame === "function") {
          window.requestAnimationFrame(clearRefreshedClass);
        } else {
          clearRefreshedClass();
        }
      }
    });
  }

  function markShippingVisualUpdated() {
    const shippingRows = document.querySelectorAll('[data-shipping-item="true"], .shipping-highlight');

    shippingRows.forEach((row) => {
      row.classList.add("valhalla-shipping-updated");
    });

    shippingVisualTimer = setTimeout(() => {
      shippingRows.forEach((row) => {
        row.classList.remove("valhalla-shipping-updated");
      });
      document.documentElement.classList.remove("valhalla-shipping-updated");
    }, 900);

    document.documentElement.classList.add("valhalla-shipping-updated");
  }

  function toNumber(value) {
    const number = Number(value);
    return Number.isFinite(number) ? number : null;
  }

  function normalizeShippingType(type) {
    return SHIPPING_TYPES.includes(type) ? type : "moto";
  }

  function isShippingVariantId(variantId) {
    return SHIPPING_VARIANT_IDS.has(Number(variantId));
  }

  function isShippingItem(item) {
    return (
      Number(item?.product_id) === SHIPPING_PRODUCT_ID ||
      isShippingVariantId(item?.variant_id) ||
      isShippingVariantId(item?.id)
    );
  }

  function getShippingItems(cart) {
    if (!cart?.items?.length) return [];

    return cart.items
      .map((item, index) => ({
        ...item,
        __line: index + 1,
      }))
      .filter(isShippingItem);
  }

  function getNonShippingItems(cart) {
    if (!cart?.items?.length) return [];
    return cart.items.filter((item) => !isShippingItem(item));
  }

  function isValidLocation(location) {
    const lat = toNumber(location?.lat);
    const lng = toNumber(location?.lng);

    return lat !== null && lng !== null && lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180;
  }

  function normalizeLocation(location) {
    if (!isValidLocation(location)) return null;

    const lat = Number(location.lat);
    const lng = Number(location.lng);

    return {
      ...location,
      lat,
      lng,
      address: typeof location.address === "string" && location.address.trim() ? location.address : "Ubicacion seleccionada",
      googleMapsUrl:
        typeof location.googleMapsUrl === "string" && location.googleMapsUrl.trim()
          ? location.googleMapsUrl
          : `https://www.google.com/maps?q=${lat},${lng}`,
    };
  }

  // =========================
  // CartService
  // =========================
  async function getCart() {
    const res = await rawFetch("/cart.js", {
      credentials: "same-origin",
      headers: {
        Accept: "application/json",
      },
    });

    if (!res.ok) {
      throw new Error(`No se pudo leer el carrito (${res.status})`);
    }

    return await res.json();
  }

  async function postCart(url, payload) {
    const res = await rawFetch(url, {
      method: "POST",
      credentials: "same-origin",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify(payload),
    });

    if (!res.ok) {
      let message = `Operacion de carrito fallida (${res.status})`;

      try {
        const error = await res.json();
        message = error?.description || error?.message || message;
      } catch (err) {
        // Keep the HTTP status message when Shopify does not return JSON.
      }

      throw new Error(message);
    }

    try {
      return await res.json();
    } catch (err) {
      return null;
    }
  }

  // =========================
  // LocationService
  // =========================
  function getLocation() {
    const data = localStorage.getItem(LOCATION_STORAGE_KEY);
    if (!data) return null;

    try {
      const location = normalizeLocation(JSON.parse(data));

      if (!location) {
        localStorage.removeItem(LOCATION_STORAGE_KEY);
        return null;
      }

      return location;
    } catch (err) {
      localStorage.removeItem(LOCATION_STORAGE_KEY);
      return null;
    }
  }

  function isLocalZone(location) {
    const normalized = normalizeLocation(location);
    if (!normalized) return false;

    const { lat, lng } = normalized;

    return lat >= 14.0 && lat <= 14.2 && lng >= -87.3 && lng <= -87.1;
  }

  // =========================
  // ShippingTypeResolver
  // =========================
  function resolveShippingType(cart) {
    let highestPriority = "moto";

    getNonShippingItems(cart).forEach((item) => {
      const type = item.properties?.shipping_type || item.properties?.["shipping_type"] || null;

      if (type === "carga") {
        highestPriority = "carga";
      } else if (type === "vehiculo" && highestPriority !== "carga") {
        highestPriority = "vehiculo";
      }
    });

    return highestPriority;
  }

  // =========================
  // ShippingRules
  // =========================
  function getVariantKey(type, isLocal) {
    return `${type}_${isLocal ? "local" : "nacional"}`;
  }

  function resolveExpectedVariantId(cart, location) {
    const normalizedLocation = normalizeLocation(location);
    if (!normalizedLocation || !getNonShippingItems(cart).length) return null;

    const type = resolveShippingType(cart);
    const key = getVariantKey(type, isLocalZone(normalizedLocation));

    return VARIANTS[key] || null;
  }

  function notifyCartObserver(reason) {
    try {
      window.CartObserver?.schedule?.(reason);
    } catch (err) {
      // Observer notifications are a convenience, not a hard dependency.
    }

    document.dispatchEvent(
      new CustomEvent("valhalla:cart-mutated", {
        detail: { reason },
      })
    );
  }

  function notifyShippingState(detail) {
    document.dispatchEvent(
      new CustomEvent("valhalla:shipping-state", {
        detail: {
          isProcessing: shippingState.isProcessing,
          pendingSync: shippingState.rerunRequested || shippingState.externalSyncPending,
          ...detail,
        },
      })
    );
  }

  function notifyShippingUpdated(detail) {
    document.dispatchEvent(
      new CustomEvent("valhalla:shipping-updated", {
        detail: {
          isProcessing: shippingState.isProcessing,
          pendingSync: shippingState.rerunRequested || shippingState.externalSyncPending,
          ...detail,
        },
      })
    );
  }

  function notifyShippingSyncComplete(detail) {
    document.dispatchEvent(
      new CustomEvent("valhalla:shipping-sync-complete", {
        detail: {
          isProcessing: false,
          pendingSync: false,
          ...detail,
        },
      })
    );
  }

  async function changeCartLine(item, quantity) {
    const payload = item.key
      ? {
          id: item.key,
          quantity,
        }
      : {
          line: item.__line,
          quantity,
        };

    return await postCart("/cart/change.js", payload);
  }

  async function updateCartLinesByKey(updates) {
    const cleanUpdates = Object.fromEntries(
      Object.entries(updates).filter(([key, quantity]) => key && Number.isFinite(Number(quantity)))
    );

    if (!Object.keys(cleanUpdates).length) return false;

    await postCart("/cart/update.js", {
      updates: cleanUpdates,
    });

    return true;
  }

  async function setCartItemQuantity(item, quantity) {
    const normalizedQuantity = Math.max(0, Number(quantity) || 0);

    if (item.key) {
      return await updateCartLinesByKey({
        [item.key]: normalizedQuantity,
      });
    }

    await changeCartLine(item, normalizedQuantity);
    return true;
  }

  function isSameCartLine(left, right) {
    if (!left || !right) return false;
    if (left.key && right.key) return left.key === right.key;

    return left.__line === right.__line;
  }

  async function removeShippingItems(items) {
    const sortedItems = [...items].sort((a, b) => b.__line - a.__line);
    const keyedUpdates = {};
    const lineFallbacks = [];

    for (const item of sortedItems) {
      if (item.key) {
        keyedUpdates[item.key] = 0;
      } else {
        lineFallbacks.push(item);
      }
    }

    await updateCartLinesByKey(keyedUpdates);

    for (const item of lineFallbacks) {
      await changeCartLine(item, 0);
    }
  }

  async function setShippingQuantity(item, quantity) {
    if (Number(item.quantity) === quantity) return false;

    await setCartItemQuantity(item, quantity);
    return true;
  }

  async function addShippingVariant(variantId) {
    await postCart("/cart/add.js", {
      id: Number(variantId),
      quantity: 1,
    });
  }

  function isShippingStable(cart, expectedVariantId) {
    const shippingItems = getShippingItems(cart);

    if (!expectedVariantId) return shippingItems.length === 0;
    if (shippingItems.length !== 1) return false;

    const shippingItem = shippingItems[0];

    return Number(shippingItem.variant_id || shippingItem.id) === Number(expectedVariantId) && Number(shippingItem.quantity) === 1;
  }

  async function normalizeShippingLines(cart, expectedVariantId, attempt = 0) {
    const desiredVariantId = isShippingVariantId(expectedVariantId) ? Number(expectedVariantId) : null;
    const freshCart = await getCart();
    const shippingItems = getShippingItems(freshCart);
    let changed = false;

    if (!desiredVariantId) {
      if (shippingItems.length) {
        await removeShippingItems(shippingItems);
        changed = true;
      }

      const validatedCart = changed ? await getCart() : freshCart;

      return {
        changed,
        cart: validatedCart,
        stable: isShippingStable(validatedCart, null),
      };
    }

    const desiredItems = shippingItems.filter(
      (item) => Number(item.variant_id) === desiredVariantId || Number(item.id) === desiredVariantId
    );

    if (desiredItems.length) {
      const keeper = desiredItems[0];
      const keyedUpdates = {};
      const lineFallbacks = [];

      shippingItems.forEach((item) => {
        const quantity = isSameCartLine(item, keeper) ? 1 : 0;

        if (Number(item.quantity) === quantity && quantity === 1 && isSameCartLine(item, keeper)) return;
        if (Number(item.quantity) === quantity && quantity === 0 && !isSameCartLine(item, keeper)) return;

        changed = true;

        if (item.key) {
          keyedUpdates[item.key] = quantity;
        } else {
          lineFallbacks.push({
            item,
            quantity,
          });
        }
      });

      await updateCartLinesByKey(keyedUpdates);

      for (const fallback of lineFallbacks) {
        await changeCartLine(fallback.item, fallback.quantity);
      }

      const validatedCart = changed ? await getCart() : freshCart;

      if (!isShippingStable(validatedCart, desiredVariantId) && attempt < 3) {
        const result = await normalizeShippingLines(validatedCart, desiredVariantId, attempt + 1);

        return {
          changed: changed || result.changed,
          cart: result.cart,
          stable: result.stable,
        };
      }

      return {
        changed,
        cart: validatedCart,
        stable: isShippingStable(validatedCart, desiredVariantId),
      };
    }

    if (shippingItems.length) {
      await removeShippingItems(shippingItems);
      changed = true;
    }

    const beforeAddCart = await getCart();
    const desiredBeforeAdd = getShippingItems(beforeAddCart).some(
      (item) => Number(item.variant_id) === desiredVariantId || Number(item.id) === desiredVariantId
    );

    if (attempt >= 3) {
      return {
        changed,
        cart: beforeAddCart,
        stable: isShippingStable(beforeAddCart, desiredVariantId),
      };
    }

    if (desiredBeforeAdd) {
      const result = await normalizeShippingLines(beforeAddCart, desiredVariantId, attempt + 1);

      return {
        changed: changed || result.changed,
        cart: result.cart,
        stable: result.stable,
      };
    }

    await addShippingVariant(desiredVariantId);
    changed = true;

    const afterAddCart = await getCart();
    const result = await normalizeShippingLines(afterAddCart, desiredVariantId, attempt + 1);

    return {
      changed: changed || result.changed,
      cart: result.cart,
      stable: result.stable,
    };
  }

  // =========================
  // ShippingUpdater
  // =========================
  async function updateShippingVariant(variantId) {
    const cart = await getCart();
    return await syncShippingVariant(cart, variantId);
  }

  async function syncShippingVariant(cart, variantId) {
    const result = await normalizeShippingLines(cart, variantId);
    return result.changed;
  }

  function getCartItemCount(cart) {
    if (Number.isFinite(Number(cart?.item_count))) return Number(cart.item_count);

    return (cart?.items || []).reduce((total, item) => total + Number(item.quantity || 0), 0);
  }

  function updateCartBubbleFallback(cart) {
    const itemCount = getCartItemCount(cart);

    document.querySelectorAll("cart-icon").forEach((cartIcon) => {
      cartIcon.classList.toggle("header-actions__cart-icon--has-cart", itemCount > 0);
    });

    document.querySelectorAll(".cart-bubble").forEach((bubble) => {
      bubble.classList.toggle("visually-hidden", itemCount === 0);
    });

    document.querySelectorAll(".cart-bubble__text-count").forEach((count) => {
      count.classList.toggle("hidden", itemCount === 0);
      count.textContent = itemCount > 0 && itemCount < 100 ? String(itemCount) : "";
    });

    try {
      sessionStorage.setItem(
        "cart-count",
        JSON.stringify({
          value: String(itemCount),
          timestamp: Date.now(),
        })
      );
    } catch (err) {
      // Session storage may be unavailable in private browsing modes.
    }
  }

  function applyCartSectionsDirect(sections = {}) {
    const itemsComponents = Array.from(document.querySelectorAll("cart-items-component")).filter(
      (component) => component instanceof HTMLElement
    );

    itemsComponents.forEach((itemsEl) => {
      const sectionId = itemsEl.sectionId || itemsEl.dataset.sectionId;
      const html = sections[sectionId];

      if (!html) return;

      const parser = new DOMParser();
      const newDoc = parser.parseFromString(html, "text/html");
      const matchingItems =
        Array.from(newDoc.querySelectorAll("cart-items-component")).find(
          (component) => component.sectionId === sectionId || component.dataset.sectionId === sectionId
        ) || newDoc.querySelector("cart-items-component");

      if (matchingItems) {
        itemsEl.innerHTML = matchingItems.innerHTML;
        itemsEl.updatedCallback?.();
      }
    });
  }

  function dispatchThemeCartUpdate(cart, sections = {}) {
    const itemCount = getCartItemCount(cart);

    updateCartBubbleFallback(cart);
    applyCartSectionsDirect(sections);

    document.dispatchEvent(
      new CustomEvent("valhalla:theme-cart-updated", {
        bubbles: true,
        detail: {
          resource: cart,
          sourceId: SHIPPING_SYNC_SOURCE,
          data: {
            itemCount,
            source: SHIPPING_SYNC_SOURCE,
            sections,
          },
        },
      })
    );
  }

  async function fetchCartSections(sectionIds) {
    if (!sectionIds.length) return {};

    const sectionUrl = new URL(window.location.pathname || "/", window.location.origin);
    sectionUrl.searchParams.set("sections", sectionIds.join(","));

    const res = await rawFetch(sectionUrl.toString(), {
      credentials: "same-origin",
      headers: {
        Accept: "application/json",
      },
    });

    if (!res.ok) return {};

    return await res.json();
  }

  function getCartSectionIds() {
    const itemsComponents = getCartItemsComponents();

    return [
      ...new Set(
        itemsComponents
          .map((component) => component.sectionId || component.dataset.sectionId)
          .filter((sectionId) => typeof sectionId === "string" && sectionId.trim())
      ),
    ];
  }

  async function refreshCartDrawer(cart = null) {
    const latestCart = cart || (await getCart());
    const itemsComponents = getCartItemsComponents();

    if (!itemsComponents.length) {
      updateCartBubbleFallback(latestCart);
      return false;
    }

    setCartDrawerVisualRefresh(itemsComponents, true);

    try {
      let didUpdate = false;
      const sectionIds = [
        ...new Set(
          itemsComponents
            .map((component) => component.sectionId || component.dataset.sectionId)
            .filter((sectionId) => typeof sectionId === "string" && sectionId.trim())
        ),
      ];

      for (const sectionId of sectionIds) {
        const res = await rawFetch(`/?sections=${encodeURIComponent(sectionId)}`, {
          credentials: "same-origin",
          headers: {
            Accept: "application/json",
          },
        });

        if (!res.ok) continue;

        const data = await res.json();
        const html = data[sectionId];

        if (!html) continue;

        const parser = new DOMParser();
        const newDoc = parser.parseFromString(html, "text/html");
        const newItems = newDoc.querySelector("cart-items-component");

        if (!newItems) continue;

        itemsComponents
          .filter((itemsEl) => (itemsEl.sectionId || itemsEl.dataset.sectionId) === sectionId)
          .forEach((itemsEl) => {
            itemsEl.innerHTML = newItems.innerHTML;
            itemsEl.updatedCallback?.();
            didUpdate = true;
          });
      }

      updateCartBubbleFallback(latestCart);

      window.updateSavedAddressUI?.();
      window.bindLocationButton?.();
      window.updateCheckoutState?.({ source: "shipping-drawer-refresh" });

      document.dispatchEvent(
        new CustomEvent("valhalla:cart-drawer-refreshed", {
          detail: {
            cart: latestCart,
            updated: didUpdate,
          },
        })
      );

      return didUpdate;
    } finally {
      setCartDrawerVisualRefresh(itemsComponents, false);
    }
  }

  async function clearLocationIfCartIsEmpty() {
    localStorage.removeItem(LOCATION_STORAGE_KEY);
    window.updateLocationButton?.();
    window.updateSavedAddressUI?.();
    window.updateCheckoutState?.();
  }

  async function applyShippingLogicOnce() {
    const cart = await getCart();
    const nonShippingItems = getNonShippingItems(cart);

    if (!cart.items.length) {
      await clearLocationIfCartIsEmpty();
      notifyShippingState({ ready: false, reason: "empty-cart" });
      return;
    }

    if (!nonShippingItems.length) {
      const result = await normalizeShippingLines(cart, null);

      if (result.changed) {
        await refreshCartDrawer();
        notifyCartObserver("shipping-removed-empty-cart");
      }

      await clearLocationIfCartIsEmpty();
      notifyShippingState({ ready: false, reason: "empty-cart" });
      return;
    }

    const location = getLocation();

    if (!location) {
      const result = await normalizeShippingLines(cart, null);

      if (result.changed) {
        await refreshCartDrawer();
        notifyCartObserver("shipping-removed-missing-location");
        notifyShippingUpdated({ changed: true, reason: "missing-location" });
      }

      notifyShippingState({ ready: false, reason: "missing-location" });
      return;
    }

    const variantId = resolveExpectedVariantId(cart, location);

    if (!variantId) {
      notifyShippingState({ ready: false, reason: "missing-variant" });
      return;
    }

    const result = await normalizeShippingLines(cart, variantId);
    const changed = result.changed;

    if (changed) {
      await refreshCartDrawer();
      notifyCartObserver("shipping-synced");
      notifyShippingUpdated({ changed: true, reason: "shipping-synced", variantId });
    }

    notifyShippingState({ ready: true, reason: "shipping-ready", variantId });
  }

  // =========================
  // MAIN
  // =========================
  async function applyShippingLogic() {
    if (shippingState.isProcessing) {
      shippingState.rerunRequested = true;
      setShippingVisualUpdating(true);
      notifyShippingState({ ready: false, reason: "sync-queued" });
      return shippingState.activePromise || Promise.resolve();
    }

    clearExternalCartMutationSync();
    shippingState.isProcessing = true;
    shippingState.lastReason = "syncing";
    setShippingVisualUpdating(true);
    notifyShippingState({ ready: false, reason: "syncing" });

    shippingState.activePromise = (async () => {
      do {
        shippingState.rerunRequested = false;

        try {
          await applyShippingLogicOnce();
        } catch (err) {
          console.error("Error calculando envio:", err);
          notifyShippingState({ ready: false, reason: "shipping-error" });
        }
      } while (shippingState.rerunRequested);
    })().finally(() => {
      shippingState.isProcessing = false;
      shippingState.activePromise = null;
      shippingState.externalSyncPending = false;
      shippingState.lastReason = "idle";
      setShippingVisualUpdating(false);
      markShippingVisualUpdated();
      notifyShippingSyncComplete({ reason: "sync-complete" });
    });

    return shippingState.activePromise;
  }

  async function validateCheckoutState() {
    try {
      const cart = await getCart();
      const location = getLocation();
      const nonShippingItems = getNonShippingItems(cart);
      const shippingItems = getShippingItems(cart);
      const expectedVariantId = resolveExpectedVariantId(cart, location);
      const shippingItem = shippingItems[0] || null;
      const isSyncing = shippingState.isProcessing || shippingState.rerunRequested || shippingState.externalSyncPending;
      const hasSingleShippingLine = shippingItems.length === 1;
      const hasCorrectShippingVariant = Number(shippingItem?.variant_id || shippingItem?.id) === Number(expectedVariantId);
      const hasShippingQuantityOne = Number(shippingItem?.quantity) === 1;
      const hasExpectedShipping = hasSingleShippingLine && hasShippingQuantityOne && hasCorrectShippingVariant;

      return {
        ready: Boolean(!isSyncing && nonShippingItems.length && location && expectedVariantId && hasExpectedShipping),
        hasProducts: nonShippingItems.length > 0,
        hasLocation: Boolean(location),
        hasShipping: hasExpectedShipping,
        hasSingleShippingLine,
        hasCorrectShippingVariant,
        hasShippingQuantityOne,
        isSyncing,
        pendingSync: shippingState.rerunRequested || shippingState.externalSyncPending,
        expectedVariantId,
        shippingCount: shippingItems.reduce((total, item) => total + Number(item.quantity || 0), 0),
      };
    } catch (err) {
      return {
        ready: false,
        hasProducts: false,
        hasLocation: Boolean(getLocation()),
        hasShipping: false,
        isSyncing: shippingState.isProcessing || shippingState.rerunRequested || shippingState.externalSyncPending,
        pendingSync: shippingState.rerunRequested || shippingState.externalSyncPending,
        expectedVariantId: null,
        shippingCount: 0,
        error: err,
      };
    }
  }

  function getSyncState() {
    return {
      isProcessing: shippingState.isProcessing,
      pendingSync: shippingState.rerunRequested || shippingState.externalSyncPending,
      lastReason: shippingState.lastReason,
    };
  }

  document.addEventListener(
    "click",
    (event) => {
      const button = event.target.closest('button[name="add"]');
      if (!button) return;

      lastShippingType = normalizeShippingType(button.dataset.shippingType);
    },
    true
  );

  function getRequestPath(resource) {
    try {
      if (resource instanceof Request) {
        return new URL(resource.url, window.location.origin).pathname;
      }

      return new URL(String(resource), window.location.origin).pathname;
    } catch (err) {
      return "";
    }
  }

  function isCartAddRequest(path) {
    return path === "/cart/add" || path === "/cart/add.js";
  }

  function isCartMutationRequest(path) {
    return ["/cart/add", "/cart/add.js", "/cart/change", "/cart/change.js", "/cart/update", "/cart/update.js", "/cart/clear", "/cart/clear.js"].includes(path);
  }

  function clearExternalCartMutationSync() {
    clearTimeout(externalCartMutationTimer);
    externalCartMutationTimer = null;
    shippingState.externalSyncPending = false;
  }

  function scheduleExternalCartMutationSync(path) {
    clearTimeout(externalCartMutationTimer);
    shippingState.externalSyncPending = true;
    notifyShippingState({ ready: false, reason: `external-cart-mutation:${path}` });

    externalCartMutationTimer = setTimeout(() => {
      externalCartMutationTimer = null;
      shippingState.externalSyncPending = false;

      applyShippingLogic().catch((err) => {
        console.error("Error sincronizando envio despues de cambio de carrito:", err);
      });
    }, path.includes("/cart/change") ? 80 : 140);
  }

  function getFormDataVariantId(formData) {
    return toNumber(formData.get("id"));
  }

  function applyShippingTypeToFormData(formData, shippingType) {
    const variantId = getFormDataVariantId(formData);
    if (isShippingVariantId(variantId)) return false;

    formData.set("properties[shipping_type]", shippingType);
    return true;
  }

  function applyShippingTypeToJsonBody(body, shippingType) {
    const data = JSON.parse(body);

    if (isShippingVariantId(data?.id)) {
      return {
        blocked: true,
        body,
      };
    }

    if (Array.isArray(data?.items)) {
      const containsShippingProduct = data.items.some((item) => isShippingVariantId(item?.id));

      if (containsShippingProduct) {
        return {
          blocked: true,
          body,
        };
      }

      data.items = data.items.map((item) => ({
        ...item,
        properties: {
          ...(item.properties || {}),
          shipping_type: item.properties?.shipping_type || shippingType,
        },
      }));
    } else {
      data.properties = {
        ...(data.properties || {}),
        shipping_type: data.properties?.shipping_type || shippingType,
      };
    }

    return {
      blocked: false,
      body: JSON.stringify(data),
    };
  }

  function applyShippingTypeToUrlEncodedBody(body, shippingType) {
    const params = body instanceof URLSearchParams ? body : new URLSearchParams(String(body));
    const variantId = toNumber(params.get("id"));

    if (isShippingVariantId(variantId)) {
      return {
        blocked: true,
        body,
      };
    }

    params.set("properties[shipping_type]", shippingType);

    return {
      blocked: false,
      body: params,
    };
  }

  function blockedShippingResponse() {
    return new Response(
      JSON.stringify({
        status: 403,
        message: "El producto de envio se administra automaticamente.",
      }),
      {
        status: 403,
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
  }

  function prepareCartAddRequest(args) {
    const config = args[1] || {};
    const body = config.body;
    const shippingType = normalizeShippingType(lastShippingType);

    if (body instanceof FormData) {
      if (!applyShippingTypeToFormData(body, shippingType)) {
        return {
          blocked: true,
          args,
        };
      }

      return {
        blocked: false,
        args,
      };
    }

    if (body instanceof URLSearchParams) {
      const result = applyShippingTypeToUrlEncodedBody(body, shippingType);
      args[1] = {
        ...config,
        body: result.body,
      };

      return {
        blocked: result.blocked,
        args,
      };
    }

    if (typeof body === "string") {
      try {
        const result = body.trim().startsWith("{")
          ? applyShippingTypeToJsonBody(body, shippingType)
          : applyShippingTypeToUrlEncodedBody(body, shippingType);

        args[1] = {
          ...config,
          body: result.body,
        };

        return {
          blocked: result.blocked,
          args,
        };
      } catch (err) {
        return {
          blocked: false,
          args,
        };
      }
    }

    return {
      blocked: false,
      args,
    };
  }

  if (!fetchState.wrapped) {
    window.fetch = async function shippingAwareFetch(...args) {
      const path = getRequestPath(args[0]);

      if (isCartAddRequest(path)) {
        const preparedRequest = prepareCartAddRequest(args);

        if (preparedRequest.blocked) {
          notifyCartObserver("blocked-manual-shipping-add");
          return blockedShippingResponse();
        }

        args = preparedRequest.args;
      }

      const response = await rawFetch.apply(this, args);

      if (isCartMutationRequest(path)) {
        notifyCartObserver(`cart-mutation:${path}`);

        if (response.ok) {
          scheduleExternalCartMutationSync(path);
        }
      }

      return response;
    };

    fetchState.wrapped = true;
  }

  return {
    applyShippingLogic,
    validateCheckoutState,
    getSyncState,
  };
})();
